import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCms } from "../CmsContext";
import { PageHeader } from "../EditorChrome";
import { formatDateOnly, timeAgo, getCollection } from "../cmsUtils";

const ACTION_VERBS = {
  create: "created",
  update: "changed",
  delete: "deleted",
  restore: "restored",
  publish: "published",
};

export function revisionSentence(rev) {
  const verb = ACTION_VERBS[rev.action] || rev.action;
  const who = rev.employeeName || "Someone";
  if (rev.action === "publish" && rev.targetKey === "site") {
    return `${who} published the website`;
  }
  const [collectionKey] = String(rev.targetKey || "").split(":");
  const collection = rev.targetType === "entry" ? getCollection(collectionKey) : null;
  const what = collection
    ? `${collection.singular.toLowerCase()} “${rev.targetLabel || rev.targetKey}”`
    : `the ${rev.targetLabel || rev.targetKey} page`;
  return `${who} ${verb} ${what}`;
}

export function OverviewPage() {
  const { api, API_BASE, publishStatus, changedCount, employee } = useCms();
  const [revisions, setRevisions] = useState([]);
  const [openTickets, setOpenTickets] = useState(null);

  useEffect(() => {
    (async () => {
      const [revRes, ticketRes] = await Promise.all([
        api.get(`${API_BASE}/revisions`, { limit: 8 }),
        api.get(`${API_BASE}/tickets`),
      ]);
      if (revRes && revRes.success) setRevisions(revRes.revisions || []);
      if (ticketRes && ticketRes.success) {
        setOpenTickets(
          (ticketRes.tickets || []).filter((t) => t.status === "open" || t.status === "in-progress")
            .length
        );
      }
    })();
  }, [api, API_BASE]);

  const firstName = employee?.firstName || employee?.fullName?.split(" ")[0] || "";

  return (
    <div className="lw-page">
      <PageHeader
        title={firstName ? `Welcome back, ${firstName}` : "Overview"}
        subtitle="Manage the content of landmarkventures.com — edits are saved as drafts and go live when you publish."
      />

      <div className="lw-statgrid">
        <div className="lw-statcard">
          <p className="lw-statcard__value">{changedCount}</p>
          <p className="lw-statcard__label">Unpublished change{changedCount === 1 ? "" : "s"}</p>
        </div>
        <div className="lw-statcard">
          <p className="lw-statcard__value">{openTickets == null ? "…" : openTickets}</p>
          <p className="lw-statcard__label">
            <Link to="../tickets">Open change requests</Link>
          </p>
        </div>
        <div className="lw-statcard">
          <p className="lw-statcard__value lw-statcard__value--sm">
            {publishStatus.lastRelease ? formatDateOnly(publishStatus.lastRelease.createdTime) : "Never"}
          </p>
          <p className="lw-statcard__label">
            Last publish
            {publishStatus.lastRelease?.triggeredBy?.fullName
              ? ` · ${publishStatus.lastRelease.triggeredBy.fullName}`
              : ""}
          </p>
        </div>
      </div>

      <div className="lw-overview__cols">
        <section className="lw-card">
          <div className="lw-card__head">
            <h2 className="lw-card__title">Recent activity</h2>
            <Link to="../activity" className="lw-card__more">
              View all
            </Link>
          </div>
          {revisions.length === 0 ? (
            <p className="lw-empty">No changes recorded yet.</p>
          ) : (
            <ul className="lw-activityfeed">
              {revisions.map((rev) => (
                <li key={rev._id} className="lw-activityfeed__item">
                  <span className={`lw-dot lw-dot--${rev.action}`} />
                  <span className="lw-activityfeed__text">{revisionSentence(rev)}</span>
                  <span className="lw-activityfeed__time">{timeAgo(rev.createdTime)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="lw-card">
          <div className="lw-card__head">
            <h2 className="lw-card__title">Jump in</h2>
          </div>
          <div className="lw-quicklinks">
            <Link to="../pages" className="lw-quicklinks__item">
              <strong>Edit page copy</strong>
              <span>Homepage, About, Services, Markets, Careers…</span>
            </Link>
            <Link to="../collections/newsroom" className="lw-quicklinks__item">
              <strong>Write a newsroom post</strong>
              <span>Publish articles with a rich-text editor</span>
            </Link>
            <Link to="../collections/team" className="lw-quicklinks__item">
              <strong>Manage the team</strong>
              <span>Add, edit or remove people on /people</span>
            </Link>
            <Link to="../tickets" className="lw-quicklinks__item">
              <strong>Request a site change</strong>
              <span>For layout tweaks and anything outside the CMS</span>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
